import { useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { CreateScenarioInput, Scenario } from "types/graphqlapi";
import {
  createScenarioMutation,
  deleteScenarioMutation,
  fetchScenarioBysubjectIdQuery,
} from "utils/apis/scenario";

/**
 * テキストをDBに入る形に最適化する
 * @param sentence
 * @param subjectId
 * @returns
 * 次のように番号を振る
 * ソートナンバー           ：        1             2               3              4               5              6              7
 * インタビューフローナンバー       ：        0             0               0              1               1              1              1
 * テンプレートナンバー：        0             1               2              0               1              2              3
 *                                          [Scenario, Template, Template, Scenario, Template, Template, Template]
 * ※ Templateの前にはカンマが付いている
 */
export const convertSentenceToSave = (
  sentence: string,
  subjectId: string,
  selectedGroupId: string
) => {
  // 改行で区切り、配列にする
  const draftSentenceList = sentence.split(/\r\n|\n/);

  const convertedSentenceList: string[] = [];
  for (let i = 0; i < draftSentenceList.length; i++) {
    const monitorMarkConvertedText = draftSentenceList[i].includes(":M:")
      ? draftSentenceList[i].replace(":M:", "${MONITOR}")
      : draftSentenceList[i];
    if (
      monitorMarkConvertedText.startsWith(":★:") ||
      monitorMarkConvertedText.startsWith(":●:")
    ) {
      convertedSentenceList.push(monitorMarkConvertedText);
    } else {
      const concatText = `${
        convertedSentenceList[convertedSentenceList.length - 1]
      }¥n${monitorMarkConvertedText}`;
      convertedSentenceList.pop();
      convertedSentenceList.push(concatText);
    }
  }

  // sort_indexを付与。インタビューフローにtemplate_no=0を指定
  const scenarioList = convertedSentenceList
    .filter((draftSentence) => draftSentence !== "")
    .map((draftSentence, index) => {
      if (draftSentence.startsWith(":★:")) {
        return {
          subject_id: subjectId,
          sort_index: index,
          scenario_no: 9999,
          template_no: 0,
          text: draftSentence.slice(3),
          groups: selectedGroupId,
        };
      } else if (draftSentence.startsWith(":●:")) {
        return {
          subject_id: subjectId,
          sort_index: index,
          scenario_no: 9999,
          template_no: 9999,
          text: draftSentence.slice(3),
          groups: selectedGroupId,
        };
      } else {
        return {
          subject_id: subjectId,
          sort_index: index,
          scenario_no: 9999,
          template_no: 0,
          text: draftSentence,
          groups: selectedGroupId,
        };
      }
    });

  // scenarioNoを付与
  let scenarioNo = 1;
  let firstRoop_scenario = true;
  for (let i = 0; i < scenarioList.length; i++) {
    if (!firstRoop_scenario && scenarioList[i].template_no === 0) {
      scenarioNo++;
    }
    scenarioList[i].scenario_no = scenarioNo;
    firstRoop_scenario = false;
  }

  // template_noを付与
  let templateNo = 1;
  let firstRoop_template = true;
  for (let j = 0; j < scenarioList.length; j++) {
    if (firstRoop_template) {
      firstRoop_template = false;
      continue;
    }

    if (scenarioList[j].template_no === 0) {
      templateNo = 1;
      continue;
    }
    scenarioList[j].template_no = templateNo;
    templateNo++;
  }

  return scenarioList;
};

const convertSentenceToDisplay = (scenarioList: Scenario[]) => {
  const draftDisplayList = scenarioList.map((scenario) => {
    let text = scenario.text;

    if (text && text.includes("${MONITOR}")) {
      text = text.replace(/\${MONITOR}/g, ":M:");
    }

    if (text && text.includes("¥n")) {
      text = text.replace(/¥n/g, "\n");
    }

    if (scenario.template_no === 0) {
      return `:★:${text}\n`;
    } else {
      return `:●:${text}\n`;
    }
  });
  return draftDisplayList.join("");
};

/**
 * DBへインタビューフローを保存する
 * @param scenarioList
 */
const insertScnarioList = async (scenarioList: CreateScenarioInput[]) => {
  await Promise.all(
    scenarioList.map(async (scenario) => {
      await createScenarioMutation(scenario);
    })
  );
};

/**
 * 同一案件に紐づくインタビューフローを削除する
 * @param subjectId
 */
const deleteSameSubjectScenario = async (subjectId: string) => {
  const scenarioList: Scenario[] = await fetchScenarioBysubjectIdQuery(
    subjectId
  );
  scenarioList &&
    (await Promise.all(
      scenarioList.map(async (scenario) => {
        await deleteScenarioMutation({ id: scenario.id });
      })
    ));
};

export const useScenario = () => {
  const history = useHistory();
  const { setLoading } = useLoadingState();
  const [sentenceState, setSentenceState] = useState<string>("");
  const { selectedGroupId } = useConsoleState();

  const createScenario = async (sentence: string, subjectId: string) => {
    // 整形
    const scenarioList = convertSentenceToSave(
      sentence,
      subjectId,
      selectedGroupId
    );
    if (scenarioList === undefined) return;
    // 消去ー＞インサート
    try {
      setLoading(true);
      await deleteSameSubjectScenario(subjectId);
      await insertScnarioList(scenarioList);
      setLoading(false);
    } catch (error) {
      console.error(error);
      alert("エラーが発生しました。");
      setLoading(false);
      history.replace("/auth/login");
    } finally {
      setLoading(false);
    }
  };

  const reflesh = async (subjectId: string) => {
    setLoading(true);
    try {
      const scenarioList: Scenario[] = await fetchScenarioBysubjectIdQuery(
        subjectId
      );
      if (scenarioList.length >= 1) {
        setSentenceState(convertSentenceToDisplay(scenarioList));
      } else {
        setSentenceState(
          `:★:項目1\n:●:テンプレート\n:●:テンプレート\n:★:項目2\n:●:テンプレート\n改行テンプレート`
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    createScenario,
    reflesh,
    sentenceState,
    setSentenceState,
  };
};
